import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import type { AppEvent } from "../api";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import {
  Column,
  sortEventsNewestFirst,
  statusMeta,
} from "../components/AccountFeed";
import { EventDetailModal, summaryFromEvent } from "../components/EventDetailModal";
import { useToast } from "../components/Toast";
import { isElectronDesktop, mutateStock } from "../desktop";
import { useLocalEventsQuery } from "../hooks/localEventsStore";
import { useLiveEvents } from "../hooks/useLiveEvents";

function mergeById(base: AppEvent[], extra: AppEvent[]): AppEvent[] {
  const map = new Map<number, AppEvent>();
  for (const e of base) map.set(e.id, e);
  for (const e of extra) map.set(e.id, e);
  return [...map.values()];
}

const NON_STASH_OPTS = {
  stockStage: "non_stash",
  limit: 2000,
  includeHidden: false,
} as const;

/** Items that arrived while Station Pause Stash was ON. */
export function NonStashPage() {
  const { user, setAssignedKeys } = useAuth();
  const { push } = useToast();
  const [selected, setSelected] = useState<AppEvent | null>(null);
  const [filter, setFilter] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [httpSeed, setHttpSeed] = useState<AppEvent[]>([]);
  const [overlay, setOverlay] = useState<AppEvent[]>([]);
  const [seedError, setSeedError] = useState<string | null>(null);
  const desktop = isElectronDesktop();
  const isAdmin = user?.role === "admin";

  const onAssignment = useCallback(
    (info: {
      resourceKey: string;
      message: string;
      assignedKeys: string[];
      events: AppEvent[];
    }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "ok");
    },
    [push, setAssignedKeys]
  );

  const onUnassignment = useCallback(
    (info: { resourceKey: string; message: string; assignedKeys: string[] }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "info");
    },
    [push, setAssignedKeys]
  );

  const { status, events: liveEvents, error } = useLiveEvents({
    enabled: Boolean(user) && isAdmin,
    onAssignment,
    onUnassignment,
  });

  const { events: localSeed } = useLocalEventsQuery(
    NON_STASH_OPTS,
    Boolean(desktop && isAdmin)
  );

  useEffect(() => {
    if (!isAdmin || desktop) return;
    let cancelled = false;
    void api<{ events: AppEvent[] }>(
      "/api/admin/events/stock?stage=non_stash&limit=1000"
    )
      .then((data) => {
        if (!cancelled) {
          setHttpSeed(data.events);
          setSeedError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSeedError(
            err instanceof Error ? err.message : "Failed to load Non Stash"
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, desktop]);

  const seed = mergeById(desktop ? localSeed : httpSeed, overlay);
  const events = useMemo(() => mergeById(seed, liveEvents), [seed, liveEvents]);

  const meta = statusMeta(status);
  const selectedLive = selected
    ? events.find((e) => e.id === selected.id) || selected
    : null;

  const items = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return events
      .filter((e) => !e.hidden && (e.stockStage || "none") === "non_stash")
      .filter((e) => {
        if (!q) return true;
        const s = summaryFromEvent(e);
        return (
          s.id.toLowerCase().includes(q) ||
          s.email.toLowerCase().includes(q) ||
          s.type.toLowerCase().includes(q)
        );
      })
      .sort(sortEventsNewestFirst);
  }, [events, filter]);

  async function sendToPending(ev: AppEvent) {
    setBusyId(ev.id);
    try {
      const moved = await mutateStock(ev.id, {
        stage: "pending",
        channel: "admin_user",
      });
      setOverlay((prev) => mergeById(prev, [moved]));
      push("Moved to Available Stock → Pending", "ok");
      setSelected(null);
    } catch (err) {
      push(err instanceof Error ? err.message : "Move failed", "error");
    } finally {
      setBusyId(null);
    }
  }

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="page page-wide page-fill">
      <header className="page-header">
        <div>
          <h1>Pending</h1>
          <p className="muted page-blurb">
            Ready logins that arrived while Pause Stash was on — not auto-enrolled
          </p>
        </div>
        <div className="page-header-actions">
          <input
            className="search header-search"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <span className={`status-chip ${meta.className}`}>
            <span className="status-dot" />
            {meta.label}
          </span>
          <span className="muted tiny">{items.length} items</span>
        </div>
      </header>

      {(error || seedError) && (
        <div className="banner error">{error || seedError}</div>
      )}

      <div className="feed-board feed-board-single">
        <Column
          title="Held during Pause Stash"
          items={items}
          empty="No Non Stash items."
          isAdmin
          onOpen={setSelected}
        />
      </div>

      {selectedLive && (
        <EventDetailModal
          event={selectedLive}
          onClose={() => setSelected(null)}
          nonStashMode
          onSendToPending={
            busyId === selectedLive.id
              ? undefined
              : () => void sendToPending(selectedLive)
          }
        />
      )}
    </div>
  );
}
